/**
 * Shared matching between a QuickBooks "Customer Balance Detail" CSV section
 * and one of an association's accounts. Extracted so the Transactions importer
 * and the Starting-Balances importer resolve sections identically — the
 * shortcode-prefix + owner-name tie-break convention lives in exactly one place.
 *
 * See also the `workorder-shortcode-account-matching` skill.
 */
import type { Account, Person } from '@/types/models';
import type { ParsedSection } from '@/utils/transactions-csv-parser';

export interface SectionMatch {
	section: ParsedSection;
	/** Parsed prefix from customer name (e.g. "742PS"). */
	accountNumberPrefix?: string;
	/** Whether the CSV row indicates this is a closed/archived record. */
	closed: boolean;
	/** Candidate accounts filtered by prefix + archived state. */
	candidates: Account[];
	/** Resolved account (1 candidate, or name-matched from multiple). */
	matched?: Account;
	/** Reason we couldn't match (no candidates, ambiguous, etc). */
	reason?: string;
}

export const parseCustomerName = (
	name: string,
): { accountNumberPrefix?: string; closed: boolean; remainder: string } => {
	const trimmed = name.trim();
	const closed = /\(CLOSED\)/i.test(trimmed);
	const m = trimmed.match(/^(\S+)\s*-\s*(.+)$/);
	if (!m) return { closed, remainder: trimmed.replace(/\(CLOSED\)/gi, '').trim() };
	return {
		accountNumberPrefix: m[1],
		closed,
		remainder: m[2].replace(/\(CLOSED\)/gi, '').replace(/\([tr-][^)]*\)/gi, '').trim(),
	};
};

/**
 * Extract candidate "owner words" (things that look like last names) from
 * the QB customer name tail. We use these to tie-break when multiple
 * accounts share an account-number prefix. Captures words 3+ chars long
 * that start with a capital letter or an apostrophe — covers "Spencer",
 * "Valentine/Dockry" (→ Valentine, Dockry), "754 Pearl LLC" (→ Pearl, LLC),
 * etc. We lowercase for case-insensitive compare.
 */
export const ownerTokens = (remainder: string): string[] => {
	const out = new Set<string>();
	for (const w of remainder.split(/[\s,/&()]+/)) {
		const t = w.trim();
		if (t.length < 3) continue;
		if (!/^[A-Za-z]/.test(t)) continue;
		out.add(t.toLowerCase());
	}
	return [...out];
};

/** Result of resolving a QB customer NAME (not a full section) to an account. */
export interface NameMatch {
	accountNumberPrefix?: string;
	closed: boolean;
	candidates: Account[];
	matched?: Account;
	reason?: string;
}

/**
 * Resolve a QB "Name" string (the account display name — "742PS - Jones",
 * "1 - ` Mittiga", "105 - Strauss / Dolan", "742PS - Jones (CLOSED)") to one of
 * an association's accounts. This is the shared convention used by every
 * importer AND the QB-bundle compiler; keep it string-keyed so the compiler can
 * call it per journal line without constructing a ParsedSection.
 *
 * Order: exact `external_name` → account-number prefix (+ archived-state) →
 * owner-name tie-break when several archived siblings share the prefix.
 */
export const resolveAccountForCustomerName = (
	customerName: string,
	accounts: Account[],
	peopleByAccountId: Map<string, Person[]>,
): NameMatch => {
	const { accountNumberPrefix, closed, remainder } = parseCustomerName(customerName);

	// Exact-name match is the strongest signal — bulk importers store the QB
	// CUST.NAME verbatim on the account. Exactly one match → done.
	const exactName = accounts.filter((a) => a.externalName === customerName);
	if (exactName.length === 1) {
		return { accountNumberPrefix, closed, candidates: exactName, matched: exactName[0] };
	}

	if (!accountNumberPrefix) {
		return { closed, candidates: [], reason: 'no account-number prefix in customer name' };
	}
	// Candidates = same prefix + archived-state match. The prefix match is
	// `account_number === "754PS"` for the active one, or `"754PS-<digit>"`
	// for closed ones — hence the regex rather than a LIKE.
	const prefixRe = new RegExp(`^${accountNumberPrefix}(?:-\\d+)?$`);
	const wantArchived = closed;
	const candidates = accounts.filter((a) =>
		a.accountNumber && prefixRe.test(a.accountNumber)
		&& (!!a.archivedAt) === wantArchived,
	);

	if (candidates.length === 0) {
		return { accountNumberPrefix, closed, candidates, reason: `no ${wantArchived ? 'archived' : 'active'} account with prefix ${accountNumberPrefix}` };
	}
	if (candidates.length === 1) {
		return { accountNumberPrefix, closed, candidates, matched: candidates[0] };
	}

	// Multiple candidates — tie-break by owner last-name match against people
	// attached to each candidate.
	const targetTokens = ownerTokens(remainder);
	let bestScore = 0;
	let bestMatch: Account | undefined;
	for (const c of candidates) {
		const people = peopleByAccountId.get(c.id) ?? [];
		let score = 0;
		for (const p of people) {
			const personTokens = [
				...ownerTokens(p.firstName ?? ''),
				...ownerTokens(p.lastName ?? ''),
			];
			for (const t of targetTokens) if (personTokens.includes(t)) score += 2;
		}
		if (score > bestScore) {
			bestScore = score;
			bestMatch = c;
		}
	}
	if (bestMatch) {
		return { accountNumberPrefix, closed, candidates, matched: bestMatch };
	}
	return {
		accountNumberPrefix, closed, candidates,
		reason: `ambiguous: ${candidates.length} archived accounts share prefix ${accountNumberPrefix} and no attached-person name matched "${remainder}"`,
	};
};

export const matchSection = (
	section: ParsedSection,
	accounts: Account[],
	peopleByAccountId: Map<string, Person[]>,
): SectionMatch => {
	const m = resolveAccountForCustomerName(section.customerName, accounts, peopleByAccountId);
	return { section, ...m };
};
